'use client';
import {
  Button,
  ContextUserType,
  IMessage,
  IReplyNavigationInitialFocus,
  IMessagesQueryParams,
  MessageBlocked,
  PostItemReactions,
  PostItemReactionsProps,
  ReplyList,
  Separator,
} from '@ui/components';
import { PostItemAuthor } from '../posts/post-item-author';
import { PostItemsMoreOptions } from '../posts/post-item-more-option';
import { MessageItemDescription } from '@ui/components/organisms/message/components/message-list/message-item-description';
import { MessageItemFiles } from '@ui/components/organisms/message/components/message-list/message-item-files';
import { CircleMinus, CirclePlus } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useReplyRefContext } from '@ui/contexts';
import { ItemUpdateMessageContentWrapper } from '../common/item-update-message-content-wrapper';
import { useMessageUpdateFormContext } from '@ui/contexts/message-update-form-context';
import { useInteractionGuard } from '@hooks/index';
import { trackCommentThreadExpanded } from '../../../../../analytics/utils';

interface Props {
  comment: IMessage;
  userType: ContextUserType;
  creator_id: string;
  user_id: string;
  enableReply: boolean;
  isPinned?: boolean;
  query: IMessagesQueryParams;
  reportLabel?: string;
  isReply?: boolean;
  isTopLevelComment?: boolean;
  main_post_ref?: string;
  isDynamicRoute?: boolean;
  communityDetails: NonNullable<IMessage['community_details']>;
  isAuthenticated: boolean;
  isFanOfCreator: boolean;
  parent_comment_thread_id?: string;
  hierarchyType: PostItemReactionsProps['hierarchyType'];
  keepThreadLineOpen?: boolean;
  filter_by_reply_id?: string;
  target_comment_id?: string;
  target_reply_id?: string;
  highlight_target?: boolean;
  /** A validated focal response used as a route-local presentation seed. */
  initialFocus?: IReplyNavigationInitialFocus | null;
  /** External dwell-driven highlight control; overrides the legacy 3s timer. */
  highlight_visible?: boolean;
  /** Set false after deliberate user scrolling. Reduced motion keeps the initial positioning pass. */
  highlight_auto_scroll?: boolean;
  /** Route generation used to re-arm scrolling for an explicit same-URL click. */
  highlight_generation_key?: string;
  /** Reports relevant visibility of the exact target row (header or body). */
  onTargetVisibilityChange?: (visible: boolean) => void;
  /** Reports late target/list layout changes that must settle before dwell. */
  onTargetLayoutChange?: () => void;
  /** Reports the nested ReplyList anchored-query readiness for reply targets. */
  onReplyTargetReadyChange?: (ready: boolean) => void;
  pagination_version?: string | number;
  moderators_ids: string[];
}
export const CommentItem = (props: Props) => {
  const { onTargetVisibilityChange, onTargetLayoutChange } = props;
  const { interceptInteraction } = useInteractionGuard();
  const [dropDownOpen, setDropDownOpen] = useState(false);
  const id = useId();
  const { setTargetMessage } = useMessageUpdateFormContext();
  const { setTargetRef, setMessage, expandedList, setExpanded, setReplyUIVersion } =
    useReplyRefContext();
  const replyRef = useRef(null);
  const updateRef = useRef(null);
  const targetRowRef = useRef<HTMLDivElement | null>(null);
  const targetMarkerRef = useRef<HTMLDivElement | null>(null);
  const scrolledTargetRef = useRef<string | null>(null);
  const targetTopRef = useRef<number | null>(null);
  const intersectionVisibleRef = useRef(false);
  const documentVisibleRef = useRef(true);

  const isOptimisticMessage = useMemo(() => {
    const idIsTemp =
      typeof props.comment?.id === 'string' && (props.comment.id as string).startsWith('temp-');
    const slugIsTemp =
      typeof props.comment?.slug === 'string' && (props.comment.slug as string).startsWith('temp-');
    const processingStatus =
      typeof (props.comment as any)?.miscellaneous?.processing_status === 'string' ||
      typeof (props.comment as any)?.processing_status === 'string';
    return idIsTemp || slugIsTemp || processingStatus;
  }, [props.comment]);

  const hasImages = useMemo(
    () =>
      Boolean(
        props.comment?.files && props.comment.files.images && props.comment.files.images.length > 0,
      ),
    [props.comment?.files],
  );

  const hadChildren = useMemo(() => {
    if (!props.comment?.child_messages) return false;
    return props.comment?.child_messages > 0;
  }, [props.comment?.child_messages]);

  const onTriggerReplyForm = interceptInteraction(() => {
    if (replyRef.current) {
      setMessage({
        ...props.comment,
        queryParams: props.query,
        child_thread_id: props.isReply
          ? props.comment.parent_thread_id
          : props.comment.child_thread_id,
        ...(props.isReply && {
          miscellaneous: {
            is_replying_to: props.comment.id,
          },
        }),
      });
      setTargetRef(props.isReply ? null : replyRef.current);
      setReplyUIVersion(props.isReply ? 2 : 1);
    }
  }, 'You are not a fan of this creator. Collect this badge to join.');

  const onUpdateTrigger = () => {
    if (updateRef.current) {
      setTargetMessage({
        ...props.comment,
        isPost: false,
        targetRef: updateRef.current,
        type: props.isReply ? 'reply' : 'comment',
        query: props.query,
      });
    }
  };

  const handleExpand = () => {
    const newExpanded = !expandedList[props.comment.id];
    setExpanded(props.comment.id, newExpanded);
    trackCommentThreadExpanded({
      commentId: props.comment.id,
      postId: props.main_post_ref,
      creatorId: props.creator_id,
      userId: props.user_id,
      expanded: newExpanded,
    });
  };

  useEffect(() => {
    if (props.keepThreadLineOpen && props.filter_by_reply_id) {
      setExpanded(props.comment.id, true);
    }
  }, [props.keepThreadLineOpen, props.filter_by_reply_id, props.comment.id, setExpanded]);

  // A focused reply is the stronger target: scrolling its parent first would
  // immediately fight with the reply scroll as pages mount. For a comment
  // notification, the comment itself remains the target row.
  const targetId = props.isReply
    ? props.target_reply_id
    : props.target_reply_id
    ? undefined
    : props.target_comment_id;
  const isTargetRow = Boolean(targetId && props.comment.id === targetId);
  const [isTargetHighlightVisible, setIsTargetHighlightVisible] = useState(false);
  useEffect(() => {
    if (props.highlight_visible !== undefined) {
      // Dwell-driven routes own the highlight lifecycle externally; the
      // internal timer must not race the accumulated-dwell expiry.
      setIsTargetHighlightVisible(Boolean(props.highlight_visible && isTargetRow));
      return;
    }
    if (!props.highlight_target || !isTargetRow) {
      setIsTargetHighlightVisible(false);
      return;
    }

    setIsTargetHighlightVisible(true);
    const timeout = window.setTimeout(() => setIsTargetHighlightVisible(false), 3000);
    return () => window.clearTimeout(timeout);
  }, [isTargetRow, props.highlight_target, props.highlight_visible]);

  const preserveTargetViewport = useCallback(() => {
    if (
      props.highlight_auto_scroll === false ||
      !targetId ||
      props.comment.id !== targetId ||
      !targetRowRef.current
    )
      return;

    const currentTop = targetRowRef.current.getBoundingClientRect().top;
    if (targetTopRef.current == null) {
      targetTopRef.current = currentTop;
    } else {
      const delta = currentTop - targetTopRef.current;
      if (Math.abs(delta) > 1 && typeof window.scrollBy === 'function') {
        window.scrollBy({ top: delta, behavior: 'auto' });
        // scrollBy changes the target's viewport coordinate by the inverse of
        // the requested amount. Store the expected post-correction position
        // immediately; the scroll listener will confirm it asynchronously.
        targetTopRef.current = currentTop - delta;
      } else {
        targetTopRef.current = currentTop;
      }
    }
  }, [props.comment.id, props.highlight_auto_scroll, targetId]);

  // A same-URL notification click keeps the row mounted. Reset the per-row
  // scroll guard for the new route generation so the explicit click can
  // orient the reader again.
  useEffect(() => {
    scrolledTargetRef.current = null;
    targetTopRef.current = null;
  }, [props.highlight_generation_key, targetId]);

  useEffect(() => {
    if (!targetId || props.comment.id !== targetId || scrolledTargetRef.current === targetId) {
      return;
    }
    // Deliberate user scrolling stops automatic scrolling. This route target is
    // in normal document flow, whose html/body scroll containers explicitly
    // use scroll-behavior:auto; reduced motion therefore keeps positioning
    // available without suppressing the initial pass.
    if (props.highlight_auto_scroll === false) return;

    const frame = requestAnimationFrame(() => {
      if (!targetRowRef.current) return;
      targetRowRef.current.scrollIntoView({ behavior: 'auto', block: 'center' });
      scrolledTargetRef.current = targetId;
      targetTopRef.current = targetRowRef.current.getBoundingClientRect().top;
    });
    return () => cancelAnimationFrame(frame);
  }, [props.comment.id, props.highlight_auto_scroll, props.highlight_generation_key, targetId]);

  // Reports exact-target-row relevance: any intersection counts for tall rows
  // (header or first body visibility is sufficient), and document visibility
  // changes pause dwell. Cleanup reports hidden so unmounted rows cannot keep
  // a stale "visible" flag accumulating dwell.
  useEffect(() => {
    const report = () => {
      if (isTargetRow) {
        onTargetVisibilityChange?.(intersectionVisibleRef.current && documentVisibleRef.current);
      }
    };
    if (!targetId || props.comment.id !== targetId || !onTargetVisibilityChange) {
      intersectionVisibleRef.current = false;
      return;
    }

    intersectionVisibleRef.current = false;
    documentVisibleRef.current = document.visibilityState === 'visible';
    const targetRow = targetMarkerRef.current || targetRowRef.current;
    if (!targetRow || typeof IntersectionObserver === 'undefined') {
      const rect = targetRow?.getBoundingClientRect();
      intersectionVisibleRef.current = Boolean(
        rect &&
          rect.bottom > 0 &&
          rect.top < (window.innerHeight || document.documentElement.clientHeight),
      );
      report();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry) {
          intersectionVisibleRef.current = entry.isIntersecting;
          report();
        }
      },
      { threshold: 0 },
    );
    observer.observe(targetRow);

    const onVisibilityChange = () => {
      documentVisibleRef.current = document.visibilityState === 'visible';
      report();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    report();

    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      intersectionVisibleRef.current = false;
      report();
    };
  }, [
    isTargetRow,
    props.comment.id,
    props.highlight_generation_key,
    onTargetVisibilityChange,
    targetId,
  ]);

  // The editor is dynamically imported and can change the list height after
  // the pagination/query effects have settled. Observe the stable list shell
  // and the target row so late form or media layout changes receive the same
  // correction and reset/report the dwell settle window.
  useEffect(() => {
    const targetRow = targetRowRef.current;
    if (
      !targetId ||
      props.comment.id !== targetId ||
      !targetRow ||
      typeof ResizeObserver === 'undefined'
    ) {
      return;
    }

    const listShell = targetRow.closest<HTMLElement>('[data-community-comment-list]');
    if (!listShell) return;

    let frame: number | null = null;
    const onLayout = () => {
      onTargetLayoutChange?.();
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        preserveTargetViewport();
      });
    };
    const observer = new ResizeObserver(onLayout);
    const rowObserver = new ResizeObserver(onLayout);
    observer.observe(listShell);
    rowObserver.observe(targetRow);

    return () => {
      observer.disconnect();
      rowObserver.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [
    preserveTargetViewport,
    props.comment.id,
    props.highlight_generation_key,
    onTargetLayoutChange,
    targetId,
  ]);

  // Keep the last measured position in sync with normal user scrolling. This
  // gives the layout effect a pre-change viewport position even when React has
  // already committed a new page or expanded list before its cleanup runs.
  useEffect(() => {
    if (!targetId || props.comment.id !== targetId) return;

    const updateTargetTop = () => {
      if (targetRowRef.current) {
        targetTopRef.current = targetRowRef.current.getBoundingClientRect().top;
      }
    };
    window.addEventListener('scroll', updateTargetTop, { passive: true });
    return () => window.removeEventListener('scroll', updateTargetTop);
  }, [props.comment.id, props.highlight_generation_key, targetId]);

  // Loading the anchored page can insert rows before the focused item. Keep
  // its viewport position stable while that page is merged into the list.
  useLayoutEffect(() => {
    preserveTargetViewport();
  }, [
    preserveTargetViewport,
    props.comment.id,
    props.query.anchor_id,
    props.query.message_ids,
    props.query.page,
    props.pagination_version,
    props.highlight_generation_key,
    targetId,
  ]);

  // For comments/replies with images that are still in a processing/optimistic state,
  // do not render the card at all. They will appear only after the background job
  // completes and the real, persisted message is delivered via the websocket flow.
  if (isOptimisticMessage && hasImages) {
    return null;
  }

  return (
    <ItemUpdateMessageContentWrapper ref={updateRef}>
      <div
        ref={targetRowRef}
        id={id}
        data-message-id={props.comment.id}
        className="overflow-hidden">
        <div ref={targetMarkerRef} className="flex w-full justify-between">
          <PostItemAuthor
            post={props.comment}
            postOwnerUserType={props.comment.user_type === 2 ? 'creator' : 'fan'}
            creatorId={props.creator_id}
            userType={props.userType}
            IsPinned={props.isPinned}
            isDynamicRoute={props.isDynamicRoute}
            communityDetails={props.communityDetails || props.comment.community_details}
            moderators_ids={props.moderators_ids || []}
          />
          <PostItemsMoreOptions
            post={props.comment}
            userType={props.userType}
            userId={props.user_id}
            enableContentFlagging={props.comment.user_id !== props.user_id}
            reportLabel={props.reportLabel}
            onUpdateTrigger={onUpdateTrigger}
            dropdownOpen={dropDownOpen}
            setDropdownOpen={setDropDownOpen}
            isReply={props.isReply}
            isTopLevelComment={Boolean(props.isTopLevelComment) && !props.isReply}
            communityDetails={props.communityDetails || props.comment.community_details}
            moderators_ids={props.moderators_ids || []}
            disableEditDelete={isOptimisticMessage}
          />
        </div>
        <div className="flex gap-2">
          <div
            onClick={handleExpand}
            className={twMerge(
              'group visible flex w-7 cursor-pointer flex-col items-center justify-between',
              hadChildren ? 'visible' : 'invisible',
            )}>
            <Separator
              orientation="vertical"
              className="group-hover:bg-korDarkGrey flex-auto flex-grow"
            />
            <Button
              onClick={handleExpand}
              type="secondary"
              size="smallIcon"
              className="h-fit w-fit min-w-fit rounded-full p-1 lg:h-fit lg:w-fit lg:min-w-fit">
              {!expandedList[props.comment.id] && (
                <CirclePlus className="text-korDarkGrey h-5 w-5" />
              )}
              {expandedList[props.comment.id] && (
                <CircleMinus className="text-korDarkGrey h-5 w-5" />
              )}
            </Button>
            <div className="pb-2"></div>
          </div>
          <div className="w-full space-y-2">
            <div
              className={twMerge(
                "bg-input relative rounded p-2 space-y-2 transition-[background-color] duration-500 ease-out before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:rounded-l before:bg-primary before:opacity-0 before:transition-opacity before:duration-500 before:ease-out before:content-[''] motion-reduce:transition-none motion-reduce:duration-0 motion-reduce:before:transition-none motion-reduce:before:duration-0",
                isTargetHighlightVisible && 'bg-korLightOrange before:opacity-100',
              )}>
              {props.comment.block && (
                <MessageBlocked userType={props.userType} creatorId={props.creator_id} />
              )}
              {isOptimisticMessage && hasImages && !props.comment.block && (
                <div className="text-xs text-gray-500">
                  {props.hierarchyType === 'reply'
                    ? 'Processing reply with image…'
                    : 'Processing comment with image…'}
                </div>
              )}
              {!props.comment.block && (
                <MessageItemDescription
                  message={props.comment}
                  className="max-w-full rounded-none bg-transparent p-0 [&_[data-youtube-embed]]:max-w-[302px]"
                />
              )}
              {!props.comment.block &&
                props.comment?.files &&
                props.comment?.files?.images?.length > 0 && (
                  <MessageItemFiles files={props.comment.files} className="max-w-full" />
                )}
              {!props.comment.block && (
                <PostItemReactions
                  query={props.query}
                  post={props.comment}
                  userType={props.userType}
                  hierarchyType={props.hierarchyType}
                  onTriggerReplyForm={onTriggerReplyForm}
                  enableReply={props.enableReply}
                  handleExpand={handleExpand}
                />
              )}
            </div>
            <div ref={replyRef}></div>
            {props.enableReply &&
              props.comment.child_thread_id &&
              expandedList[props.comment.id] && (
                <ReplyList
                  {...props}
                  comment={props.comment}
                  parent_comment_thread_id={
                    props.isReply ? props.comment.child_thread_id : undefined
                  }
                  parent_pagination_version={props.pagination_version}
                  onTargetReadyChange={props.onReplyTargetReadyChange}
                  moderators_ids={props.moderators_ids || []}
                />
              )}
          </div>
        </div>
      </div>
    </ItemUpdateMessageContentWrapper>
  );
};
